import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Plus, Copy, RotateCcw, Power, Pencil, AlertTriangle, ShieldAlert, ArrowUp, ArrowDown, Trash2, PhoneCall, Search, X } from "lucide-react";
import { ROLE_LABELS, ROLE_BADGE, USER_ROLE_KEYS, isOperationalRole } from "@/lib/roles";
import { ClientAccessPicker } from "@/components/ClientAccessPicker";
import { DeactivateUserModal } from "@/components/DeactivateUserModal"; // Fix AS

const RESET_PHRASE = "REINICIAR";

function DwellingFields({ value, onChange, clients, prefix }) {
  const selected = clients.find((c) => c.id === value.client_access?.[0]?.client_id);
  const isBarrio = selected?.category === "barrio";
  return (
    <div className="field">
      <label>Identificación del domicilio *</label>
      <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 8px" }}>
        El vigilador necesita este dato para identificar a la persona en el control de acceso.
      </p>
      {isBarrio ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <input placeholder="Manzana *" value={value.dwelling_block || ""} onChange={(e) => onChange({ ...value, dwelling_block: e.target.value })} data-testid={`${prefix}-dwelling-block`} />
          <input placeholder="Casa *" value={value.dwelling_house || ""} onChange={(e) => onChange({ ...value, dwelling_house: e.target.value })} data-testid={`${prefix}-dwelling-house`} />
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <input placeholder="Piso *" value={value.dwelling_floor || ""} onChange={(e) => onChange({ ...value, dwelling_floor: e.target.value })} data-testid={`${prefix}-dwelling-floor`} />
          <input placeholder="Depto *" value={value.dwelling_unit || ""} onChange={(e) => onChange({ ...value, dwelling_unit: e.target.value })} data-testid={`${prefix}-dwelling-unit`} />
        </div>
      )}
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "supervisor", phone: "", default_client_id: "", client_access: [], dwelling_floor: "", dwelling_unit: "", dwelling_block: "", dwelling_house: "" });
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "supervisor", phone: "", default_client_id: "", client_access: [], dwelling_floor: "", dwelling_unit: "", dwelling_block: "", dwelling_house: "" });
  const [editBusy, setEditBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [pwPromptFor, setPwPromptFor] = useState(null);
  const [pwPromptValue, setPwPromptValue] = useState("");
  const [pwPromptBusy, setPwPromptBusy] = useState(false);
  const [sosSettings, setSosSettings] = useState(null);
  const [sosBusy, setSosBusy] = useState(false);
  const [escalationContacts, setEscalationContacts] = useState(null);
  const [escalationForm, setEscalationForm] = useState({ name: "", phone: "" });
  const [escalationAddBusy, setEscalationAddBusy] = useState(false);
  const [sitesOverview, setSitesOverview] = useState([]);
  const [editSiteIds, setEditSiteIds] = useState([]);
  const [editClientIds, setEditClientIds] = useState([]);
  const [siteSearch, setSiteSearch] = useState("");
  const [deactivateFor, setDeactivateFor] = useState(null); // Fix AS: { user, mode: "desactivar" | "liberar" }

  // Fix AC (2026-09-18): eliminar usuario conservando su historial.
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewBusy, setDeletePreviewBusy] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleteIrreversibleAck, setDeleteIrreversibleAck] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const loadClients = async () => {
    try { const { data } = await api.get("/clients"); setClients(data); } catch {}
  };
  const loadSitesOverview = async () => {
    try { const { data } = await api.get("/clients/sites-overview"); setSitesOverview(data); } catch {}
  };
  const loadSosSettings = async () => {
    try { const { data } = await api.get("/settings/sos"); setSosSettings(data); } catch {}
  };
  const loadEscalation = async () => {
    try { const { data } = await api.get("/sos/escalation-contacts"); setEscalationContacts(data); } catch {}
  };
  useEffect(() => { load(); loadClients(); loadSitesOverview(); loadSosSettings(); loadEscalation(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/users", form);
      setTempPw({ email: data.email, password: data.temp_password, name: data.name });
      if (data.phone_warning) toast.info(data.phone_warning);
      setForm({ name: "", email: "", role: "supervisor", phone: "", default_client_id: "", client_access: [], dwelling_floor: "", dwelling_unit: "", dwelling_block: "", dwelling_house: "" });
      setOpen(false); load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const openResetPassword = (u) => { setPwPromptFor(u); setPwPromptValue(""); };

  const submitResetPassword = async (e) => {
    e.preventDefault();
    const u = pwPromptFor;
    const value = pwPromptValue.trim();
    if (value && value.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    setPwPromptBusy(true);
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, value ? { new_password: value } : {});
      setTempPw({ email: u.email, password: data.temp_password, name: u.name });
      setPwPromptFor(null);
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setPwPromptBusy(false); }
  };

  const toggle = async (u) => {
    // Fix AS (2026-09-25): desactivar muestra antes sus turnos futuros y deja liberarlos como vacantes.
    if (u.active) { setDeactivateFor({ user: u, mode: "desactivar" }); return; }
    if (!window.confirm(`Activar a ${u.name}?`)) return;
    try { await api.patch(`/users/${u.id}`, { active: true }); toast.success("Usuario activado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openDeletePreview = async (u) => {
    setDeletePreviewBusy(true);
    setDeleteConfirmName(""); setDeleteIrreversibleAck(false);
    try {
      const { data } = await api.get(`/users/${u.id}/delete-preview`);
      setDeletePreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeletePreviewBusy(false); }
  };

  const closeDeletePreview = () => { setDeletePreview(null); setDeleteConfirmName(""); setDeleteIrreversibleAck(false); };

  const submitDelete = async () => {
    const d = deletePreview;
    if (!d) return;
    if (deleteConfirmName.trim().toLowerCase() !== d.user_name.trim().toLowerCase()) {
      return toast.error("El nombre escrito no coincide");
    }
    if (d.can_hard_delete && !deleteIrreversibleAck) {
      return toast.error("Tildá que entendés que la acción es irreversible");
    }
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/users/${d.user_id}${d.can_hard_delete ? "/permanent" : ""}`);
      setDeleteResult({ ...data, permanent: !!d.can_hard_delete });
      closeDeletePreview();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleteBusy(false); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", email: u.email || "", role: u.role, phone: u.phone || "", default_client_id: u.default_client_id || "", client_access: u.client_access || [],
      dwelling_floor: u.dwelling_floor || "", dwelling_unit: u.dwelling_unit || "", dwelling_block: u.dwelling_block || "", dwelling_house: u.dwelling_house || "" });
    const siteIds = [], clientIds = [];
    sitesOverview.forEach((c) => {
      if (c.sites.length === 0) {
        if (c.assigned_vigilador_ids.includes(u.id)) clientIds.push(c.client_id);
      } else {
        c.sites.forEach((s) => { if (s.assigned_vigilador_ids.includes(u.id)) siteIds.push(s.id); });
      }
    });
    setEditSiteIds(siteIds); setEditClientIds(clientIds); setSiteSearch("");
  };

  const toggleEditSite = (sid) => setEditSiteIds((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
  const toggleEditClient = (cid) => setEditClientIds((prev) => (prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]));

  const applySiteAssignments = async (uid) => {
    const tasks = [];
    sitesOverview.forEach((c) => {
      if (c.sites.length === 0) {
        const was = c.assigned_vigilador_ids.includes(uid);
        const now = editClientIds.includes(c.client_id);
        if (was !== now) {
          const next = now ? [...c.assigned_vigilador_ids, uid] : c.assigned_vigilador_ids.filter((x) => x !== uid);
          tasks.push(api.patch(`/clients/${c.client_id}`, { assigned_vigilador_ids: next }));
        }
      } else {
        c.sites.forEach((s) => {
          const was = s.assigned_vigilador_ids.includes(uid);
          const now = editSiteIds.includes(s.id);
          if (was !== now) {
            const next = now ? [...s.assigned_vigilador_ids, uid] : s.assigned_vigilador_ids.filter((x) => x !== uid);
            tasks.push(api.patch(`/sites/${s.id}`, { assigned_vigilador_ids: next }));
          }
        });
      }
    });
    if (tasks.length) await Promise.all(tasks);
  };

  const submitEdit = async (e) => {
    e.preventDefault(); setEditBusy(true);
    try {
      const { data } = await api.patch(`/users/${editing.id}`, editForm);
      if (isOperationalRole(editForm.role)) await applySiteAssignments(editing.id);
      toast.success("Usuario actualizado");
      if (data.phone_warning) toast.info(data.phone_warning);
      setEditing(null); load(); loadSitesOverview();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setEditBusy(false); }
  };

  const saveSosSettings = async (supervisorId) => {
    setSosBusy(true);
    try {
      const { data } = await api.patch("/settings/sos", { default_sos_supervisor_id: supervisorId || null });
      setSosSettings(data);
      toast.success("Configuración de SOS actualizada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSosBusy(false); }
  };

  const addEscalation = async (e) => {
    e.preventDefault();
    if (!escalationForm.name.trim() || !escalationForm.phone.trim()) return;
    setEscalationAddBusy(true);
    try {
      const { data } = await api.post("/sos/escalation-contacts", escalationForm);
      if (data.phone_warning) toast.info(data.phone_warning);
      setEscalationForm({ name: "", phone: "" });
      loadEscalation();
      toast.success("Contacto agregado a la cadena");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setEscalationAddBusy(false); }
  };

  const editEscalationField = (id, field, value) => {
    setEscalationContacts((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const saveEscalationRow = async (c) => {
    try {
      const { data } = await api.patch(`/sos/escalation-contacts/${c.id}`, { name: c.name, phone: c.phone });
      if (data.phone_warning) toast.info(data.phone_warning);
      setEscalationContacts((prev) => prev.map((x) => (x.id === c.id ? { ...x, phone: data.phone } : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteEscalation = async (id) => {
    if (!window.confirm("¿Eliminar este contacto de la cadena de escalamiento?")) return;
    try { await api.delete(`/sos/escalation-contacts/${id}`); loadEscalation(); toast.success("Contacto eliminado"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const moveEscalation = async (idx, dir) => {
    const arr = [...escalationContacts];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= arr.length) return;
    [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
    setEscalationContacts(arr);
    try { await api.post("/sos/escalation-contacts/reorder", { ordered_ids: arr.map((c) => c.id) }); }
    catch (e) { toast.error(formatApiError(e)); loadEscalation(); }
  };

  const submitResetTestData = async (e) => {
    e.preventDefault(); setResetBusy(true);
    try {
      const { data } = await api.post("/admin/reset-test-data", { confirm: resetText });
      setResetResult(data);
      setResetText("");
      load();
      toast.success("Datos de prueba eliminados");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setResetBusy(false); }
  };

  const filteredUsers = users
    ? users.filter((u) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.phone || "").toLowerCase().includes(q);
      })
    : users;

  return (
    <div style={{ padding: "20px 24px 60px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Equipo</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 0", color: "#0A0A0A", letterSpacing: "-0.02em" }} data-testid="users-title">
            Usuarios {users && <span data-testid="users-total-count">({users.length})</span>}
          </h1>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="user-new"><Plus size={14} style={{ marginRight: 6 }}/>Nuevo usuario</button>
      </div>

      {users && users.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }} data-testid="users-role-breakdown">
          {USER_ROLE_KEYS.map((role) => {
            const count = users.filter((u) => u.role === role).length;
            if (count === 0) return null;
            return (
              <span key={role} className={`badge ${ROLE_BADGE[role] || "gray"}`} data-testid={`users-role-count-${role}`}>
                {ROLE_LABELS[role]}: {count}
              </span>
            );
          })}
        </div>
      )}

      {users && users.length > 0 && (
        <div style={{ position: "relative", maxWidth: 360, marginBottom: 18 }}>
          <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--invict-text-muted)" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email o teléfono..."
            data-testid="users-search"
            style={{ width: "100%", padding: "10px 34px", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2 }}
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} data-testid="users-search-clear"
              style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--invict-text-muted)", padding: 4 }}>
              <X size={14} />
            </button>
          )}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {users == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 80 }} /></div>}
        {users && users.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "var(--invict-text-muted)" }}>Solo está creado tu usuario admin. Agregá supervisores y vigiladores.</div>}
        {users && users.length > 0 && filteredUsers.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--invict-text-muted)" }} data-testid="users-search-empty">Ningún usuario coincide con "{search}".</div>
        )}
        {filteredUsers && filteredUsers.map((u) => (
          <div key={u.id} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr auto", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--invict-bone-3)", alignItems: "center" }} data-testid={`user-row-${u.id}`}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14 }}>{u.name}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{u.phone || ""}</div>
            </div>
            <div style={{ fontSize: 12 }}>{u.email}</div>
            <div><span className={`badge ${ROLE_BADGE[u.role] || "gray"}`}>{ROLE_LABELS[u.role] || u.role}</span>
              {isOperationalRole(u.role) && (u.assigned_sites_count ?? 0) > 0 && (
                <span className="badge gray" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`user-sites-count-${u.id}`}>{u.assigned_sites_count} sede{u.assigned_sites_count === 1 ? "" : "s"}</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{u.last_login ? new Date(u.last_login).toLocaleString("es-AR") : "Nunca ingresó"}</div>
            <div><span className={`badge ${u.active ? "green" : "gray"}`}>{u.active ? "Activo" : "Inactivo"}</span></div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => openEdit(u)} className="btn-ghost" style={{ padding: "5px 8px", fontSize: 10 }} title="Editar datos" data-testid={`user-edit-${u.id}`}><Pencil size={11}/></button>
              <button onClick={() => openResetPassword(u)} className="btn-ghost" style={{ padding: "5px 8px", fontSize: 10 }} title="Resetear contraseña" data-testid={`user-reset-${u.id}`}><RotateCcw size={11}/></button>
              <button onClick={() => toggle(u)} className="btn-ghost" style={{ padding: "5px 8px", fontSize: 10, color: u.active ? "#BF3437" : "#16a34a", borderColor: u.active ? "#BF3437" : "#16a34a" }} data-testid={`user-toggle-${u.id}`}><Power size={11}/></button>
              {!u.active && <button onClick={() => setDeactivateFor({ user: u, mode: "liberar" })} className="btn-ghost" style={{ padding: "5px 8px", fontSize: 10 }} title="Liberar turnos futuros (quedan como vacantes)" data-testid={`user-release-${u.id}`}>Liberar turnos</button>}
              <button onClick={() => openDeletePreview(u)} className="btn-ghost" style={{ padding: "5px 8px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} title="Eliminar usuario" disabled={deletePreviewBusy} data-testid={`user-delete-${u.id}`}><Trash2 size={11}/></button>
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginTop: 28 }} data-testid="sos-settings-zone">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <ShieldAlert size={20} style={{ color: "#0A0A0A", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.15em", color: "var(--invict-text-muted)", fontWeight: 800, textTransform: "uppercase" }}>Configuración global de SOS</div>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: "4px 0 6px", color: "#0A0A0A" }}>Supervisor de SOS por defecto</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 12px", maxWidth: 620 }}>
              Se usa como último recurso para rutear una alerta cuando el objetivo no tiene un supervisor de SOS
              configurado, o cuando la alerta salió sin objetivo asignado. Así ningún SOS queda sin destinatario.
            </p>
            <select
              value={sosSettings?.default_sos_supervisor_id || ""}
              onChange={(e) => saveSosSettings(e.target.value)}
              disabled={sosBusy || !users}
              data-testid="sos-default-supervisor-select"
              style={{ maxWidth: 320 }}
            >
              <option value="">Sin configurar</option>
              {(users || []).filter((u) => u.role === "supervisor").map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }} data-testid="escalation-admin-zone">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <PhoneCall size={20} style={{ color: "#0A0A0A", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.15em", color: "var(--invict-text-muted)", fontWeight: 800, textTransform: "uppercase" }}>Configuración general de SOS</div>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: "4px 0 6px", color: "#0A0A0A" }}>Cadena de contactos de escalamiento</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 14px", maxWidth: 640 }}>
              Referencias adicionales, en orden, para escalar por teléfono si el primero no responde (Base, gerencia, dirección).
              Se muestra en la pantalla de Alertas y en la confirmación del vigilador tras disparar el SOS.
              <b> No reemplaza el ruteo automático al supervisor del objetivo</b>, es un complemento.
            </p>

            {escalationContacts == null ? (
              <div className="skel" style={{ height: 60 }} />
            ) : (
              <div style={{ marginBottom: 14 }}>
                {escalationContacts.map((c, idx) => (
                  <div key={c.id} data-testid={`escalation-row-${c.id}`}
                    style={{ display: "grid", gridTemplateColumns: "auto 2fr 1.4fr auto", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <button type="button" onClick={() => moveEscalation(idx, -1)} disabled={idx === 0} className="btn-ghost" style={{ padding: "2px 4px" }} data-testid={`escalation-up-${c.id}`}><ArrowUp size={11} /></button>
                      <button type="button" onClick={() => moveEscalation(idx, 1)} disabled={idx === escalationContacts.length - 1} className="btn-ghost" style={{ padding: "2px 4px" }} data-testid={`escalation-down-${c.id}`}><ArrowDown size={11} /></button>
                    </div>
                    <input value={c.name} onChange={(e) => editEscalationField(c.id, "name", e.target.value)} onBlur={() => saveEscalationRow(c)}
                      data-testid={`escalation-edit-name-${c.id}`} style={{ fontSize: 13, fontWeight: 700 }} />
                    <input value={c.phone || ""} onChange={(e) => editEscalationField(c.id, "phone", e.target.value)} onBlur={() => saveEscalationRow(c)}
                      data-testid={`escalation-edit-phone-${c.id}`} style={{ fontSize: 12 }} />
                    <button type="button" onClick={() => deleteEscalation(c.id)} className="btn-ghost" style={{ padding: "5px 8px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`escalation-delete-${c.id}`}><Trash2 size={11} /></button>
                  </div>
                ))}
                {escalationContacts.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)", padding: "10px 0" }}>Sin contactos configurados todavía.</div>}
              </div>
            )}

            <form onSubmit={addEscalation} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div className="field" style={{ margin: 0, flex: "1 1 200px" }}>
                <label>Nombre / cargo</label>
                <input value={escalationForm.name} onChange={(e) => setEscalationForm({ ...escalationForm, name: e.target.value })} placeholder="ej. Sup Bilbao" data-testid="escalation-add-name" />
              </div>
              <div className="field" style={{ margin: 0, flex: "1 1 160px" }}>
                <label>Teléfono</label>
                <input value={escalationForm.phone} onChange={(e) => setEscalationForm({ ...escalationForm, phone: e.target.value })} placeholder="ej. 261 516 9800" data-testid="escalation-add-phone" />
              </div>
              <button type="submit" className="btn-primary" disabled={escalationAddBusy} data-testid="escalation-add-submit">
                <Plus size={13} style={{ marginRight: 6 }} />{escalationAddBusy ? "Agregando..." : "Agregar contacto"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Fix AN (2026-09-25): herramienta de pruebas; en producción vaciaba datos reales. Solo con REACT_APP_SHOW_RESET_TEST_DATA=1. */}
      {process.env.REACT_APP_SHOW_RESET_TEST_DATA === "1" && (
      <div className="card" style={{ marginTop: 20, border: "1px solid #BF3437", background: "rgba(191,52,55,0.04)" }} data-testid="maintenance-zone">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <AlertTriangle size={20} style={{ color: "#BF3437", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.15em", color: "#BF3437", fontWeight: 800, textTransform: "uppercase" }}>Zona de mantenimiento</div>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: "4px 0 6px", color: "#0A0A0A" }}>Reiniciar datos de prueba</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 12px", maxWidth: 620 }}>
              Elimina usuarios de prueba (email <code>test_*@x.com</code> o nombre "TEST ...") y clientes "TEST_...",
              y vacía la actividad acumulada (alertas de pánico, visitas, novedades, solicitudes, servicios, rondas y chat).
              <b> Los usuarios y clientes reales, sus puntos de ronda, elementos de trabajo, manual/capacitaciones,
              guardias pasivas y configuración de SOS NO se tocan.</b> Esta acción es irreversible.
            </p>
            <button className="btn-ghost" style={{ borderColor: "#BF3437", color: "#BF3437" }} onClick={() => setResetOpen(true)} data-testid="open-reset-test-data">
              Reiniciar datos de prueba
            </button>
          </div>
        </div>
      </div>
      )}

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h3>Nuevo usuario</h3>
            <p>Se va a generar una contraseña temporal. Copiala — sólo se muestra una vez.</p>
            <div className="field"><label>Nombre completo *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name"/></div>
            <div className="field"><label>Email *</label><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email"/></div>
            <div className="field"><label>Rol</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                {USER_ROLE_KEYS.filter((r) => r !== "admin").map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                <option value="admin">{ROLE_LABELS.admin}</option>
              </select>
            </div>
            <div className="field"><label>Teléfono</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="ej. 261 516 9800" data-testid="user-phone"/></div>
            {["cliente", "cliente_admin", "inquilino"].includes(form.role) ? (
              <div className="field">
                <label>Objetivos que puede ver (portal del cliente)</label>
                <ClientAccessPicker clients={clients} value={form.client_access} onChange={(v) => setForm({ ...form, client_access: v })} />
                <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "4px 0 0" }}>
                  {form.role === "cliente_admin"
                    ? "Además de ver, esta persona puede aprobar/rechazar reservas del SUM y ver el total de horas/Visitas e Ingresos de estos objetivos."
                    : form.role === "inquilino"
                    ? "El inquilino solo ve su propio domicilio: reservar el SUM, autorizar retiro/ingreso de elementos, personal autorizado propio y botón SOS."
                    : "Acceso de solo lectura. Si no tildás ninguna sede de un objetivo, ve todas las sedes de ese objetivo."}
                </p>
              </div>
            ) : (
            <div className="field"><label>Objetivo asignado por defecto (opcional)</label>
              <select value={form.default_client_id} onChange={(e) => setForm({ ...form, default_client_id: e.target.value })} data-testid="user-default-client">
                <option value="">Sin asignar</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "4px 0 0" }}>
                Si el usuario dispara un SOS sin tener una ronda activa/de hoy ni una visita registrada, se usa este objetivo.
              </p>
            </div>
            )}
            {form.role === "inquilino" && <DwellingFields value={form} onChange={setForm} clients={clients} prefix="user" />}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="user-submit">{busy ? "Guardando..." : "Crear usuario"}</button>
            </div>
          </form>
        </div>
      )}

      {pwPromptFor && (
        <div className="modal-overlay" onClick={() => setPwPromptFor(null)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitResetPassword} data-testid="reset-password-modal">
            <h3>Resetear contraseña</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>
              El sistema no puede mostrar la contraseña actual de <b>{pwPromptFor.name}</b>: se guarda cifrada y es imposible recuperarla. Lo que se hace es asignarle una nueva.
            </p>
            <div className="field">
              <label>Nueva contraseña (opcional, mínimo 6 caracteres)</label>
              <input type="text" placeholder="Dejá vacío para generar una aleatoria" value={pwPromptValue} onChange={(e) => setPwPromptValue(e.target.value)} data-testid="reset-password-input" />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => setPwPromptFor(null)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={pwPromptBusy} data-testid="reset-password-submit">{pwPromptBusy ? "Guardando..." : "Confirmar reseteo"}</button>
            </div>
          </form>
        </div>
      )}

      {tempPw && (
        <div className="modal-overlay">
          <div className="modal" data-testid="temp-password-modal">
            <h3>Contraseña temporal generada</h3>
            <p>Copiala y pasásela al usuario por un canal seguro. <b>No se va a volver a mostrar.</b></p>
            <div style={{ background: "var(--invict-bone-2)", padding: 14, borderRadius: 2, marginBottom: 14, borderLeft: "4px solid #BF3437" }}>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Usuario</div>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>{tempPw.name} · {tempPw.email}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Contraseña</div>
              <div style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 800, color: "#0A0A0A", letterSpacing: "0.05em" }}>{tempPw.password}</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-ghost" style={{ flex: 1 }} onClick={() => { navigator.clipboard.writeText(tempPw.password); toast.success("Copiado"); }} data-testid="copy-password"><Copy size={14} style={{ marginRight: 6 }} />Copiar</button>
              <button className="btn-primary" style={{ flex: 1 }} onClick={() => setTempPw(null)} data-testid="close-password">Listo</button>
            </div>
          </div>
        </div>
      )}
      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitEdit} data-testid="user-edit-modal">
            <h3>Editar usuario</h3>
            <div className="field"><label>Nombre completo *</label><input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="user-edit-name"/></div>
            <div className="field"><label>Email *</label><input type="email" required value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="user-edit-email"/></div>
            <div className="field"><label>Rol</label>
              <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} data-testid="user-edit-role">
                {USER_ROLE_KEYS.filter((r) => r !== "admin").map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                <option value="admin">{ROLE_LABELS.admin}</option>
              </select>
            </div>
            <div className="field"><label>Teléfono</label><input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Ej: +5491122222222" data-testid="user-edit-phone"/></div>
            <p style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>El teléfono se usa para el contacto de SOS.</p>
            {["cliente", "cliente_admin", "inquilino"].includes(editForm.role) ? (
              <div className="field">
                <label>Objetivos que puede ver (portal del cliente)</label>
                <ClientAccessPicker clients={clients} value={editForm.client_access} onChange={(v) => setEditForm({ ...editForm, client_access: v })} />
                <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "4px 0 0" }}>
                  {editForm.role === "cliente_admin"
                    ? "Además de ver, esta persona puede aprobar/rechazar reservas del SUM y ver el total de horas/Visitas e Ingresos de estos objetivos."
                    : editForm.role === "inquilino"
                    ? "El inquilino solo ve su propio domicilio: reservar el SUM, autorizar retiro/ingreso de elementos, personal autorizado propio y botón SOS."
                    : "Acceso de solo lectura. Si no tildás ninguna sede de un objetivo, ve todas las sedes de ese objetivo."}
                </p>
              </div>
            ) : (
            <div className="field"><label>Objetivo asignado por defecto (opcional)</label>
              <select value={editForm.default_client_id} onChange={(e) => setEditForm({ ...editForm, default_client_id: e.target.value })} data-testid="user-edit-default-client">
                <option value="">Sin asignar</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            )}
            {editForm.role === "inquilino" && <DwellingFields value={editForm} onChange={setEditForm} clients={clients} prefix="user-edit" />}
            <p style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
              Si el vigilador dispara un SOS sin tener una ronda activa/de hoy ni una visita registrada, se usa este objetivo.
            </p>
            {isOperationalRole(editForm.role) && (
              <div className="field">
                <label>Sedes asignadas</label>
                <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 8px" }}>
                  Marcá en qué sedes trabaja esta persona (puede ser de objetivos distintos). Si un objetivo no tiene sedes cargadas, se marca el objetivo entero. Esto es lo que usan las rondas automáticas para asignar el turno — se guarda en cada sede/objetivo, no en el usuario.
                </p>
                {sitesOverview.length > 5 && (
                  <input value={siteSearch} onChange={(e) => setSiteSearch(e.target.value)} placeholder="Buscar objetivo..."
                    style={{ marginBottom: 8, width: "100%", padding: "8px 10px", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2 }}
                    data-testid="user-edit-sites-search" />
                )}
                <div style={{ maxHeight: 260, overflowY: "auto", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2, padding: 8 }} data-testid="user-edit-sites-list">
                  {sitesOverview.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Todavía no hay objetivos cargados.</div>}
                  {sitesOverview
                    .filter((c) => c.client_name.toLowerCase().includes(siteSearch.trim().toLowerCase()))
                    .map((c) => (
                      <div key={c.client_id} style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 4 }}>{c.client_name}</div>
                        {c.sites.length === 0 ? (
                          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", padding: "2px 0 2px 8px" }}>
                            <input type="checkbox" checked={editClientIds.includes(c.client_id)} onChange={() => toggleEditClient(c.client_id)} data-testid={`user-edit-site-client-${c.client_id}`} />
                            Todo el objetivo (sin sedes) <span style={{ color: "var(--invict-text-muted)" }}>&middot; {c.assigned_vigilador_ids.length} persona(s)</span>
                          </label>
                        ) : (
                          c.sites.map((s) => (
                            <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", padding: "2px 0 2px 8px" }}>
                              <input type="checkbox" checked={editSiteIds.includes(s.id)} onChange={() => toggleEditSite(s.id)} data-testid={`user-edit-site-${s.id}`} />
                              {s.name} <span style={{ color: "var(--invict-text-muted)" }}>&middot; {s.assigned_vigilador_ids.length} persona(s)</span>
                            </label>
                          ))
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={editBusy} data-testid="user-edit-submit">{editBusy ? "Guardando..." : "Guardar cambios"}</button>
            </div>
          </form>
        </div>
      )}

      {deactivateFor && (
        <DeactivateUserModal user={deactivateFor.user} mode={deactivateFor.mode}
          onClose={() => setDeactivateFor(null)} onDone={() => { setDeactivateFor(null); load(); }} />
      )}
      {resetOpen && (
        <div className="modal-overlay" onClick={() => { setResetOpen(false); setResetText(""); }}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitResetTestData} data-testid="reset-test-data-modal">
            <h3 style={{ color: "#BF3437" }}>Confirmar reinicio de datos de prueba</h3>
            <p style={{ fontSize: 13 }}>
              Esta acción es <b>irreversible</b>: borra usuarios/clientes de prueba y vacía alertas, visitas,
              novedades, solicitudes, servicios, rondas y chat de TODA la plataforma (no solo lo de prueba).
              Los usuarios/clientes reales y su configuración quedan intactos.
            </p>
            <div className="field">
              <label>Escribí <b>{RESET_PHRASE}</b> para confirmar</label>
              <input value={resetText} onChange={(e) => setResetText(e.target.value)} autoFocus data-testid="reset-test-data-confirm-input"/>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => { setResetOpen(false); setResetText(""); }}>Cancelar</button>
              <button type="submit" className="btn-primary" style={{ background: "#BF3437" }}
                disabled={resetBusy || resetText.trim().toUpperCase() !== RESET_PHRASE}
                data-testid="reset-test-data-submit">
                {resetBusy ? "Reiniciando..." : "Reiniciar datos de prueba"}
              </button>
            </div>
          </form>
        </div>
      )}

      {resetResult && (
        <div className="modal-overlay">
          <div className="modal" data-testid="reset-test-data-result-modal">
            <h3>Datos de prueba eliminados</h3>
            <div style={{ background: "var(--invict-bone-2)", padding: 14, borderRadius: 2, marginBottom: 14, fontSize: 12, lineHeight: 1.6 }}>
              <div>Usuarios de prueba eliminados: <b>{resetResult.deleted_users}</b></div>
              <div>Clientes de prueba eliminados: <b>{resetResult.deleted_clients}</b></div>
              <div>Puntos de ronda de esos clientes eliminados: <b>{resetResult.deleted_test_patrol_points}</b></div>
              <div style={{ marginTop: 8, borderTop: "1px solid var(--invict-bone-3)", paddingTop: 8 }}>
                Actividad vaciada — alertas: <b>{resetResult.wiped?.panic_alerts ?? 0}</b>,
                {" "}visitas: <b>{resetResult.wiped?.visit_logs ?? 0}</b>,
                {" "}novedades: <b>{resetResult.wiped?.news ?? 0}</b>,
                {" "}solicitudes: <b>{resetResult.wiped?.internal_requests ?? 0}</b>,
                {" "}servicios: <b>{resetResult.wiped?.services ?? 0}</b>,
                {" "}rondas: <b>{resetResult.wiped?.patrols ?? 0}</b>,
                {" "}mensajes de chat: <b>{resetResult.wiped?.chat_messages ?? 0}</b>.
              </div>
            </div>
            <button className="btn-primary" style={{ width: "100%" }} onClick={() => setResetResult(null)} data-testid="close-reset-test-data-result">Listo</button>
          </div>
        </div>
      )}

      {deletePreview && (
        <div className="modal-overlay" onClick={closeDeletePreview}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="user-delete-preview-modal">
            <h3 style={{ color: "#BF3437" }}>Eliminar usuario</h3>
            <div style={{ background: "var(--invict-bone-2)", padding: 12, borderRadius: 2, marginBottom: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 14 }}>{deletePreview.user_name}</div>
              <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>{deletePreview.user_email} · {ROLE_LABELS[deletePreview.user_role] || deletePreview.user_role}</div>
            </div>

            {deletePreview.can_hard_delete ? (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "rgba(191,52,55,0.08)", padding: 10, borderRadius: 4, marginBottom: 12 }} data-testid="user-delete-hard-notice">
                <ShieldAlert size={14} style={{ color: "#BF3437", flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 12, color: "#0A0A0A" }}>Esta cuenta no tiene ninguna referencia (ni historial ni configuración activa) — se puede eliminar por completo. Es <b>irreversible</b>.</div>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 6 }}>Esta cuenta tiene actividad registrada. Se va a marcar como eliminada, <b>conservando todo su historial</b> y liberando su email:</p>
                {deletePreview.history.filter((h) => h.count > 0).length > 0 && (
                  <div style={{ fontSize: 12, marginBottom: 10 }} data-testid="user-delete-history-list">
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Historial que se conserva ({deletePreview.history_total}):</div>
                    {deletePreview.history.filter((h) => h.count > 0).map((h) => (
                      <div key={h.label} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                        <span>{h.label}</span><b>{h.count}</b>
                      </div>
                    ))}
                  </div>
                )}
                {deletePreview.config.filter((c) => c.count > 0).length > 0 && (
                  <div style={{ fontSize: 12, marginBottom: 10 }} data-testid="user-delete-config-list">
                    <div style={{ fontWeight: 700, marginBottom: 4, color: "#BF3437" }}>Configuración activa que se va a quitar ({deletePreview.config_total}):</div>
                    {deletePreview.config.filter((c) => c.count > 0).map((c) => (
                      <div key={c.label} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                        <span>{c.label}</span><b>{c.count}</b>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="field">
              <label>Escribí <b>{deletePreview.user_name}</b> para confirmar</label>
              <input value={deleteConfirmName} onChange={(e) => setDeleteConfirmName(e.target.value)} autoFocus data-testid="user-delete-confirm-name-input" />
            </div>

            {deletePreview.can_hard_delete && (
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, cursor: "pointer", margin: "8px 0" }}>
                <input type="checkbox" checked={deleteIrreversibleAck} onChange={(e) => setDeleteIrreversibleAck(e.target.checked)} data-testid="user-delete-irreversible-ack" style={{ marginTop: 2 }} />
                Entiendo que esta acción es irreversible y borra la cuenta por completo.
              </label>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={closeDeletePreview}>Cancelar</button>
              <button
                type="button"
                className="btn-primary"
                style={{ background: "#BF3437" }}
                disabled={
                  deleteBusy ||
                  deleteConfirmName.trim().toLowerCase() !== deletePreview.user_name.trim().toLowerCase() ||
                  (deletePreview.can_hard_delete && !deleteIrreversibleAck)
                }
                onClick={submitDelete}
                data-testid="user-delete-confirm-submit"
              >
                {deleteBusy ? "Eliminando..." : deletePreview.can_hard_delete ? "Eliminar definitivamente" : "Eliminar (conservando historial)"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteResult && (
        <div className="modal-overlay">
          <div className="modal" data-testid="user-delete-result-modal">
            <h3>{deleteResult.permanent ? "Usuario eliminado por completo" : "Usuario eliminado"}</h3>
            {deleteResult.permanent ? (
              <p style={{ fontSize: 13 }}><b>{deleteResult.user_name}</b> fue borrado de la base sin dejar rastro (no tenía ninguna referencia).</p>
            ) : (
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                <p><b>{deleteResult.user_name}</b> quedó marcado como eliminado. Su historial se conserva, su email quedó libre y su sesión se cortó.</p>
                <div style={{ background: "var(--invict-bone-2)", padding: 10, borderRadius: 2 }}>
                  <div>Referencias de supervisor quitadas: <b>{deleteResult.removed_supervisor_refs}</b></div>
                  <div>Referencias de vigilador/sede quitadas: <b>{deleteResult.removed_vigilador_refs}</b></div>
                  <div>Referencias de SOS quitadas: <b>{deleteResult.removed_sos_refs}</b></div>
                  <div>Turnos futuros de cronograma quitados: <b>{deleteResult.removed_future_schedule}</b></div>
                </div>
              </div>
            )}
            <button className="btn-primary" style={{ width: "100%", marginTop: 12 }} onClick={() => setDeleteResult(null)} data-testid="close-user-delete-result">Listo</button>
          </div>
        </div>
      )}
    </div>
  );
}
